import { useCallback, useEffect, useState } from "react";
import { api } from "../api";

type PanelSsl = {
  domain: string;
  adminDomains?: string[];
  userDomains?: string[];
  allDomains?: string[];
  provider: string;
  status: string;
  expiresAt?: string | null;
  forceHttps?: boolean;
  adminPort?: number;
  userPort?: number;
  lastError?: string | null;
  note?: string;
  certPath?: string | null;
};

/**
 * Install Let's Encrypt (or self-signed fallback) for the RealPanel hostnames
 * and proxy HTTPS → admin :7001 / user :7002.
 */
export function PanelDomainSslCard({
  isStaff,
  isReseller
}: {
  isStaff: boolean;
  isReseller?: boolean;
}) {
  const canManage = isStaff || isReseller;
  const [ssl, setSsl] = useState<PanelSsl | null>(null);
  const [hostname, setHostname] = useState("");
  const [domain, setDomain] = useState("");
  const [forceHttps, setForceHttps] = useState(true);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [adminPort, setAdminPort] = useState(7001);
  const [userPort, setUserPort] = useState(7002);

  const load = useCallback(async () => {
    if (!canManage) return;
    setError("");
    try {
      const res = await api<{
        ok: boolean;
        ssl: PanelSsl;
        hostname: string;
        adminPort: number;
        userPort: number;
      }>("/api/v1/panel-ssl/status");
      setSsl(res.ssl || null);
      setHostname(res.hostname || "");
      setAdminPort(res.adminPort || 7001);
      setUserPort(res.userPort || 7002);
      if (!domain) {
        setDomain(res.ssl?.domain || res.hostname || "");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [canManage, domain]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!canManage) return null;

  const issue = async () => {
    const d = domain.trim() || hostname.trim();
    if (!d) {
      setError(
        "Enter a panel domain (e.g. vps.example.com). DNS A record must point to this server."
      );
      return;
    }
    setBusy(true);
    setError("");
    setMsg("");
    try {
      const res = await api<{ ok: boolean; ssl: PanelSsl }>(
        "/api/v1/panel-ssl/issue",
        {
          method: "POST",
          body: JSON.stringify({ domain: d, forceHttps })
        }
      );
      setSsl(res.ssl);
      const adminHost =
        (res.ssl?.adminDomains && res.ssl.adminDomains[0]) ||
        (d.startsWith("real.") ? d : `real.${d.replace(/^real\./, "")}`);
      const userHost =
        (res.ssl?.userDomains && res.ssl.userDomains[0]) ||
        `realpanel.${d.replace(/^real\./, "").replace(/^realpanel\./, "")}`;
      setMsg(
        res.ssl?.provider === "letsencrypt"
          ? `Panel SSL installed. Domain: https://${adminHost}/ (admin) · https://${userHost}/ (user). IP fallback still works: http://SERVER_IP:${adminPort} / :${userPort}.`
          : res.ssl?.note ||
              `Panel SSL applied (${res.ssl?.provider || "unknown"}). Domain: https://${adminHost}/ · https://${userHost}/. IP:port still works.`
      );
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    if (!isStaff) {
      setError("Only server admins can revoke panel SSL.");
      return;
    }
    if (!confirm("Remove panel domain SSL and return to HTTP-only proxy?"))
      return;
    setBusy(true);
    setError("");
    setMsg("");
    try {
      await api("/api/v1/panel-ssl/revoke", {
        method: "POST",
        body: "{}"
      });
      setMsg("Panel SSL removed.");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const active = ssl?.status === "active";
  const exp = ssl?.expiresAt
    ? new Date(ssl.expiresAt).toLocaleDateString()
    : "—";

  return (
    <div
      className="rp-panel"
      style={{
        padding: 18,
        marginBottom: 16,
        border: "1px solid var(--line)",
        borderRadius: 14,
        background: "#fff"
      }}
    >
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "flex-start",
          marginBottom: 12
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 750 }}>
            Panel Domain SSL
          </h2>
          <p
            className="faint"
            style={{ margin: "6px 0 0", fontSize: 13, maxWidth: 640 }}
          >
            Secure panel hostnames with Let&apos;s Encrypt. Naming:{" "}
            <strong className="mono">real.domain.com</strong> (admin / WHM) ·{" "}
            <strong className="mono">realpanel.domain.com</strong> (user) —
            never nested like cpanel.real.domain.com.
          </p>
          <p
            className="faint"
            style={{ margin: "8px 0 0", fontSize: 12.5, maxWidth: 640, lineHeight: 1.45 }}
          >
            <strong>Access always works both ways:</strong> after SSL you can use
            the domain <em>and</em> the server IP with ports. First install /
            fallback:{" "}
            <span className="mono">http://SERVER_IP:{adminPort}</span> (admin) ·{" "}
            <span className="mono">http://SERVER_IP:{userPort}</span> (user).
            Domain never disables IP:port login.
          </p>
        </div>
        <span
          className={`rp-status ${
            active ? "rp-status-ok" : "rp-status-warn"
          }`}
        >
          <span className="rp-status-dot" />
          {active
            ? `${ssl?.provider === "letsencrypt" ? "Let's Encrypt" : ssl?.provider || "Active"}`
            : "Not installed"}
        </span>
      </div>

      {msg ? <div className="success-banner">{msg}</div> : null}
      {error ? <div className="error-banner">{error}</div> : null}
      {ssl?.note ? (
        <div className="error-banner" style={{ fontSize: 13 }}>
          {ssl.note}
        </div>
      ) : null}
      {ssl?.lastError && active ? (
        <p className="faint" style={{ fontSize: 12 }}>
          Last LE note: {ssl.lastError}
        </p>
      ) : null}

      <div
        className="form-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) auto auto",
          gap: 10,
          alignItems: "end",
          marginTop: 8
        }}
      >
        <div className="field" style={{ margin: 0 }}>
          <label>Panel domain (admin host or apex)</label>
          <input
            className="mono"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder={hostname || "real.example.com"}
            disabled={busy}
          />
          <span className="faint" style={{ fontSize: 12 }}>
            Enter <span className="mono">real.netmoov.com</span> or{" "}
            <span className="mono">netmoov.com</span>. DNS A records required for{" "}
            <span className="mono">real.&lt;domain&gt;</span> (admin WHM) and{" "}
            <span className="mono">realpanel.&lt;domain&gt;</span> (user panel)
            only — both must point to this server.
          </span>
        </div>
        <label
          className="rp-check"
          style={{ margin: 0, whiteSpace: "nowrap", paddingBottom: 8 }}
        >
          <input
            type="checkbox"
            checked={forceHttps}
            onChange={(e) => setForceHttps(e.target.checked)}
            disabled={busy}
          />
          Force HTTPS on domain
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            style={{ width: "auto", marginTop: 0 }}
            disabled={busy}
            onClick={() => void issue()}
          >
            {busy
              ? "Installing…"
              : active
                ? "Reinstall / Renew"
                : "Install Panel SSL"}
          </button>
          {isStaff && active ? (
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => void revoke()}
            >
              Remove SSL
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={busy}
            onClick={() => void load()}
          >
            Refresh
          </button>
        </div>
      </div>

      {active ? (
        <dl
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 10,
            margin: "14px 0 0",
            fontSize: 13
          }}
        >
          <div>
            <dt className="faint" style={{ fontSize: 11, fontWeight: 650 }}>
              EXPIRES
            </dt>
            <dd style={{ margin: "2px 0 0", fontWeight: 650 }}>{exp}</dd>
          </div>
          <div>
            <dt className="faint" style={{ fontSize: 11, fontWeight: 650 }}>
              ADMIN URL (WHM)
            </dt>
            <dd style={{ margin: "2px 0 0" }}>
              <a
                href={`https://${(ssl?.adminDomains && ssl.adminDomains[0]) || ssl?.domain || domain}/`}
                target="_blank"
                rel="noreferrer"
              >
                https://
                {(ssl?.adminDomains && ssl.adminDomains[0]) ||
                  ssl?.domain ||
                  domain}
                /
              </a>
            </dd>
          </div>
          <div>
            <dt className="faint" style={{ fontSize: 11, fontWeight: 650 }}>
              IP ACCESS (ALWAYS)
            </dt>
            <dd style={{ margin: "2px 0 0" }} className="mono">
              :{adminPort} admin · :{userPort} user
            </dd>
          </div>
          <div>
            <dt className="faint" style={{ fontSize: 11, fontWeight: 650 }}>
              USER URL (cPanel)
            </dt>
            <dd style={{ margin: "2px 0 0" }}>
              <a
                href={`https://${(ssl?.userDomains && ssl.userDomains[0]) || "realpanel.example.com"}/`}
                target="_blank"
                rel="noreferrer"
              >
                https://
                {(ssl?.userDomains && ssl.userDomains[0]) || "—"}
                /
              </a>
            </dd>
          </div>
          <div>
            <dt className="faint" style={{ fontSize: 11, fontWeight: 650 }}>
              COVERED
            </dt>
            <dd
              className="mono"
              style={{ margin: "2px 0 0", fontSize: 12, wordBreak: "break-all" }}
            >
              {(ssl?.allDomains || []).join(", ") || "—"}
            </dd>
          </div>
        </dl>
      ) : null}
    </div>
  );
}
